// src/pages/TenantsPage.js
'use strict';

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

export default function TenantsPage() {
  const navigate = useNavigate();

  // Listado de tenants
  const [tenants, setTenants] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  // Edición simple
  const [openEdit, setOpenEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    tenantName: '',
    db_host: '',
    db_port: '',
  });

  // Notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // ─────────────────────────────────────────────
  // API: cargar tenants (sin eslint react-hooks plugin)
  // ─────────────────────────────────────────────
  const loadTenants = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit,
        search: search.trim() || undefined,
      };
      const res = await api.get('/tenants', { params });
      setTenants(res.data?.tenants || []);
      setTotal(res.data?.meta?.total || 0);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Error cargando tenants',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Carga automática al cambiar page/limit (búsqueda manual con botón)
  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const params = {
          page: page + 1,
          limit,
          search: search.trim() || undefined,
        };
        const res = await api.get('/tenants', { params });
        setTenants(res.data?.tenants || []);
        setTotal(res.data?.meta?.total || 0);
      } catch (err) {
        console.error(err);
        setSnackbar({
          open: true,
          message: 'Error cargando tenants',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    run();
    // eslint no plugin: no usamos react-hooks/exhaustive-deps acá
  }, [page, limit]); // intencional: no recarga al tipear search

  const handleSearch = async () => {
    setPage(0);
    await loadTenants();
  };

  // ─────────────────────────────────────────────
  // Edición simple
  // ─────────────────────────────────────────────
  const handleOpenEdit = (tenant) => {
    setEditingId(tenant.id);
    setFormData({
      tenantName: tenant.name,
      db_host: tenant.db_host || '',
      db_port: tenant.db_port || '',
    });
    setOpenEdit(true);
  };

  const handleCloseEdit = () => setOpenEdit(false);

  const handleChangeEdit = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async () => {
    try {
      const payload = { name: formData.tenantName };
      await api.put(`/tenants/${editingId}`, payload);

      setSnackbar({
        open: true,
        message: 'Tenant actualizado',
        severity: 'success',
      });

      handleCloseEdit();
      await loadTenants();
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Error actualizando tenant',
        severity: 'error',
      });
    }
  };

  // ─────────────────────────────────────────────
  // Eliminar tenant
  // ─────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar tenant (incluye BD y datos asociados)?')) return;

    try {
      await api.delete(`/tenants/${id}`);

      setSnackbar({
        open: true,
        message: 'Tenant eliminado',
        severity: 'info',
      });

      // Si borraste el último de la página, retrocede una página si corresponde
      if (tenants.length === 1 && page > 0) {
        setPage((p) => p - 1);
      } else {
        await loadTenants();
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Error eliminando tenant',
        severity: 'error',
      });
    }
  };

  // ─────────────────────────────────────────────
  // Paginación
  // ─────────────────────────────────────────────
  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setLimit(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ─────────────────────────────────────────────
  // Nuevo tenant -> redirige al Wizard nuevo
  // ─────────────────────────────────────────────
  const handleGoCreateTenant = () => {
    navigate('/tenants/create'); // ajusta si tu ruta es distinta
  };

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Administrar Tenants
      </Typography>

      {/* Barra de búsqueda y acciones */}
      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item>
          <TextField
            label="Buscar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Grid>

        <Grid item>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleSearch}
            disabled={loading}
          >
            Buscar
          </Button>
        </Grid>

        <Grid item xs />

        <Grid item>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleGoCreateTenant}
          >
            Nuevo
          </Button>
        </Grid>
      </Grid>

      {/* Tabla de tenants */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>DB Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Creado</TableCell>
                <TableCell>Actualizado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tenants.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.id}</TableCell>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>{t.db_name}</TableCell>
                  <TableCell>{t.status}</TableCell>
                  <TableCell>
                    {t.created_at && new Date(t.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {t.updated_at && new Date(t.updated_at).toLocaleString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenEdit(t)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(t.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {!tenants.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No hay tenants creados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={limit}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 20]}
          />
        </Paper>
      )}

      {/* Diálogo edición simple */}
      <Dialog open={openEdit} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Tenant</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Nombre"
            name="tenantName"
            value={formData.tenantName}
            onChange={handleChangeEdit}
          />
          <TextField
            fullWidth
            margin="normal"
            label="DB Host"
            name="db_host"
            value={formData.db_host}
            disabled
          />
          <TextField
            fullWidth
            margin="normal"
            label="DB Port"
            name="db_port"
            type="number"
            value={formData.db_port}
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveEdit}>
            Actualizar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
